/**
 * See {@link https://github.com/Contrast-Security-Inc/skeletor/tree/develop/packages/foundations Skeletor Foundations} for replacement interface.
 * @deprecated
 * @see {@link https://github.com/Contrast-Security-Inc/skeletor/blob/develop/packages/foundations/src/types/index.ts ThemeColors}
 */
export interface ThemeColors {
  readonly red: string
  readonly orange: string
  readonly yellow: string
  readonly yellowGreen: string
  readonly yellowHighlight: string
  readonly darkTeal: string
  readonly green: string
  readonly blue: string
  readonly purple: string
  readonly teal: string
  readonly lightTeal: string
  readonly limedSpruce: string
  readonly lightBlue: string
  readonly brightPurple: string
  readonly darkGreen: string
  readonly selection: string
  readonly linkLight: string
  readonly black: string
  readonly gray000: string
  readonly gray005: string
  readonly gray010: string
  readonly gray020: string
  readonly gray030: string
  readonly gray040: string
  readonly gray050: string
  readonly gray055: string
  readonly gray060: string
  readonly gray070: string
  readonly white: string
  readonly transparent: string
  readonly legacyGrayMiddle: string
}

/**
 * @deprecated
 */
export interface SecondaryThemeColors {
  readonly chartVulnerableAreaTop: string
  readonly chartVulnerableAreaBottom: string
  readonly chartVulnerableAreaBg: string
  readonly chartExercisedAreaTop: string
  readonly chartExercisedAreaBottom: string
  readonly chartExercisedAreaBg: string
  readonly chartDiscoveredAreaTop: string
  readonly chartDiscoveredAreaBottom: string
  readonly chartDiscoveredAreaBg: string
  readonly chartDisabledLine: string
  readonly gray040WithMiddleOpacity: string
  readonly panelBorder: string
  readonly panelBoxShadow: string
  readonly chartCriticalAreaBottom: string
  readonly chartCriticalAreaTop: string
  readonly chartCriticalAreaBg: string
  readonly chartHighAreaBottom: string
  readonly chartHighAreaTop: string
  readonly chartHighAreaBg: string
  readonly chartMediumAreaTop: string
  readonly chartMediumAreaBottom: string
  readonly chartMediumAreaBg: string
  readonly chartLowAreaTop: string
  readonly chartLowAreaBottom: string
  readonly chartLowAreaBg: string
  readonly chartNoteAreaTop: string
  readonly chartNoteAreaBottom: string
  readonly chartNoteAreaBg: string
  readonly chartRemediatedAreaTop: string
  readonly chartRemediatedAreaBottom: string
  readonly chartRemediatedAreaBg: string
  readonly chartDuplicateAreaTop: string
  readonly chartDuplicateAreaBottom: string
  readonly chartDuplicateAreaBg: string
  readonly chartNewlyIntroducedAreaTop: string
  readonly chartNewlyIntroducedAreaBottom: string
  readonly chartNewlyIntroducedAreaBg: string
  readonly tagRemove: string
}

/**
 * See {@link https://github.com/Contrast-Security-Inc/skeletor/tree/develop/packages/foundations Skeletor Foundations} for replacement interface.
 * @deprecated
 * @see {@link https://github.com/Contrast-Security-Inc/skeletor/blob/develop/packages/foundations/src/types/index.ts ThemeColors}
 */
export type KeyOfThemeColors = keyof ThemeColors

/**
 * See {@link https://github.com/Contrast-Security-Inc/skeletor/tree/develop/packages/foundations Skeletor Foundations} for replacement interface.
 * @deprecated
 * @see {@link https://github.com/Contrast-Security-Inc/skeletor/blob/develop/packages/foundations/src/types/index.ts ThemeFonts}
 */
export interface ThemeFontFamilies {
  readonly primary: string
  readonly primaryCondensed: string
  readonly secondary: string
  readonly code: string
  readonly handwriting: string
}

/**
 * See {@link https://github.com/Contrast-Security-Inc/skeletor/tree/develop/packages/foundations Skeletor Foundations} for replacement interface.
 * @deprecated
 * @see {@link https://github.com/Contrast-Security-Inc/skeletor/blob/develop/packages/foundations/src/types/index.ts ThemeFonts}
 */
export interface ThemeFamilyFontWeights {
  readonly primary: {
    readonly light: 300
    readonly regular: 400
    readonly medium: 500
    readonly bold: 700
  }
  readonly primaryCondensed: {
    readonly regular: 400
    readonly bold: 700
  }
  readonly secondary: {
    readonly thin: 300
    readonly regular: 400
    readonly bold: 700
    readonly boldest: 900
  }
}

export interface ThemePadding {
  readonly comboboxInput: string
}

/**
 * See {@link https://github.com/Contrast-Security-Inc/skeletor/tree/develop/packages/foundations Skeletor Foundations} for replacement interface.
 * @deprecated
 * @see {@link https://github.com/Contrast-Security-Inc/skeletor/blob/develop/packages/foundations/src/types/index.ts SkeletorTheme}
 */
export interface Theme {
  readonly colors: ThemeColors
  readonly secondaryColors: SecondaryThemeColors
  readonly space: number[]
  readonly breakpoints: number[]
  readonly fontFamilies: ThemeFontFamilies
  readonly fontWeights: ThemeFamilyFontWeights
  readonly padding: ThemePadding
}

/**
 * See {@link https://github.com/Contrast-Security-Inc/skeletor/tree/develop/packages/foundations Skeletor Foundations} for replacement design tokens.
 * @deprecated
 * @see {@link https://github.com/Contrast-Security-Inc/skeletor/blob/develop/packages/foundations/src/tokens/js/theme.tsx theme}
 */
export const theme: Theme = {
  colors: {
    red: '#e63025',
    orange: '#f78a31',
    yellow: '#f7b600',
    yellowGreen: '#dddd6d',
    darkTeal: '#0da1a9',
    green: '#aecd43',
    blue: '#258cbf',
    purple: '#5e4482',
    teal: '#3cc3b2',
    lightTeal: '#6ee3e9',
    limedSpruce: '#31434e',
    lightBlue: '#8fdffa',
    brightPurple: '#993ba3',
    darkGreen: '#76993a',
    selection: '#e7f4f2',
    linkLight: '#9edcdf',
    yellowHighlight: '#ffffc6',
    /**
     * This is our custom grayscale palette. Note that "black" and "white" are not pure black and white. They
     * are the darkest and lightest shades of gray in our custom palette.
     *
     * Grayscale is historically difficult to choose an appropriate naming system, the following system allows
     * for "in between" shades to be added later if UX decides to do so.
     */
    black: '#292929', // legacy gray: gray-darker
    gray000: '#363a39',
    gray005: '#666',
    gray010: '#999', // legacy gray: gray-lighter-darker
    gray020: '#9ea7ac',
    gray030: '#bfbfbf', // legacy gray: gray-light
    gray040: '#dcdcdc', // legacy gray: gray-lighter-middle
    gray050: '#eee', // legacy gray: gray-lighter
    gray055: '#f4f4f4',
    gray060: '#f8f8f8',
    gray070: '#fcfcfc',
    white: '#fff',
    transparent: 'transparent',
    legacyGrayMiddle: '#808080'
  },
  secondaryColors: {
    chartVulnerableAreaBottom: 'rgba(230, 48, 37, 0.75)',
    chartVulnerableAreaTop: 'rgba(230, 48, 37, 0.35)',
    chartVulnerableAreaBg: 'rgba(230, 48, 37, 0.15)',
    chartExercisedAreaBottom: 'rgba(60, 195, 178, 0.75)',
    chartExercisedAreaTop: 'rgba(60, 195, 178, 0.35)',
    chartExercisedAreaBg: 'rgba(60, 195, 178,  0.15)',
    chartDiscoveredAreaBottom: 'rgba(220, 220, 220, 0.75)',
    chartDiscoveredAreaTop: 'rgba(220, 220, 220, 0.35)',
    chartDiscoveredAreaBg: 'rgba(220, 220, 220, 0.15)',
    chartDisabledLine: '#dddddd',
    gray040WithMiddleOpacity: '#dcdcdc69',
    panelBorder: '#e9e9e9',
    panelBoxShadow: 'rgba(221,216,216,0.5)',
    chartCriticalAreaBottom: 'rgba(230, 48, 37, 0.75)',
    chartCriticalAreaTop: 'rgba(230, 48, 37, 0.35)',
    chartCriticalAreaBg: 'rgba(230, 48, 37, 0.15)',
    chartHighAreaBottom: 'rgba(247, 138, 49, 0.75)',
    chartHighAreaTop: 'rgba(247, 138, 49, 0.35)',
    chartHighAreaBg: 'rgba(247, 138, 49, 0.15)',
    chartMediumAreaBottom: 'rgba(247, 182, 0, 0.75)',
    chartMediumAreaTop: 'rgba(247, 182, 0, 0.35)',
    chartMediumAreaBg: 'rgba(247, 182, 0, 0.15)',
    chartLowAreaBottom: 'rgba(220, 220, 220, 0.75)',
    chartLowAreaTop: 'rgba(220, 220, 220, 0.35)',
    chartLowAreaBg: 'rgba(220, 220, 220, 0.15)',
    chartNoteAreaBottom: 'rgba(235, 235, 235, 0.75)',
    chartNoteAreaTop: 'rgba(235, 235, 235, 0.35)',
    chartNoteAreaBg: 'rgba(238, 238, 238, 0.15)',
    chartRemediatedAreaBottom: 'rgba(191, 191, 191, 0.15)',
    chartRemediatedAreaTop: 'rgba(191, 191, 191, 0.75)',
    chartRemediatedAreaBg: 'rgba(191, 191, 191, 0.15)',
    chartDuplicateAreaBottom: 'rgba(128, 128, 128, 0.15)',
    chartDuplicateAreaTop: 'rgba(128, 128, 128, 0.75)',
    chartDuplicateAreaBg: 'rgba(128, 128, 128, 0.15)',
    chartNewlyIntroducedAreaBottom: 'rgba(90, 189, 195, 0.50)',
    chartNewlyIntroducedAreaTop: 'rgba(90, 189, 195, 1)',
    chartNewlyIntroducedAreaBg: 'rgba(90, 189, 195, 0.15)',
    tagRemove: '#aaaaaa'
  },
  space: [0, 8, 18, 32, 64],
  breakpoints: [40, 52, 64],
  fontFamilies: {
    primary: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
    primaryCondensed: "'Roboto Condensed', 'Helvetica Neue', Arial, sans-serif",
    secondary: "'Lato', 'Helvetica Neue', Arial, sans-serif",
    code: "'Courier', sans-serif",
    handwriting: "'Architects Daughter', 'Helvetica Neue', Arial, sans-serif"
  },
  fontWeights: {
    primary: {
      light: 300,
      regular: 400,
      medium: 500,
      bold: 700
    },
    primaryCondensed: {
      regular: 400,
      bold: 700
    },
    secondary: {
      thin: 300,
      regular: 400,
      bold: 700,
      boldest: 900
    }
  },

  padding: { comboboxInput: '3px 8px 4px' }
}
